import { useEffect, useMemo, useState } from "react";
import { useAccount, usePublicClient, useReadContract, useWalletClient } from "wagmi";
import { formatUnits, isAddress, parseEther, parseUnits } from "viem";
import { polygon } from "viem/chains";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useNetworkSwitch } from "./form/hooks/useNetworkSwitch";
import { erc20Abi } from "./form/utils/contractConfig";
import { FAIR_LAUNCH_POOL_ABI, FAIR_LAUNCH_POOL_ADDRESS } from "@/components/coin-detail/FairLaunchPoolConfig";
import { supabaseExtended } from "@/integrations/supabase/extendedClient";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";

const formatDurationHours = (seconds: string) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return "Enter a duration to see the hour conversion.";
  return `≈ ${(value / 3600).toLocaleString(undefined, { maximumFractionDigits: 2 })} hours`;
};

export function ExistingCoinFairLaunchForm() {
  const { address, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient({ chainId: polygon.id });
  const { ensureCorrectNetwork } = useNetworkSwitch();
  const { toast } = useToast();
  const [tokenAddress, setTokenAddress] = useState("");
  const [tokenAmount, setTokenAmount] = useState("");
  const [duration, setDuration] = useState("86400");
  const [target, setTarget] = useState("20");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validTokenAddress = isAddress(tokenAddress);
  const token = validTokenAddress ? tokenAddress as `0x${string}` : undefined;
  const { data: tokenName } = useReadContract({ address: token, abi: erc20Abi, functionName: "name", chainId: polygon.id, query: { enabled: !!token } });
  const { data: tokenSymbol } = useReadContract({ address: token, abi: erc20Abi, functionName: "symbol", chainId: polygon.id, query: { enabled: !!token } });
  const { data: tokenDecimals } = useReadContract({ address: token, abi: erc20Abi, functionName: "decimals", chainId: polygon.id, query: { enabled: !!token } });
  const { data: tokenBalance } = useReadContract({ address: token, abi: erc20Abi, functionName: "balanceOf", args: address ? [address] : undefined, chainId: polygon.id, query: { enabled: !!token && !!address } });
  const { data: existingPool } = useReadContract({ address: FAIR_LAUNCH_POOL_ADDRESS, abi: FAIR_LAUNCH_POOL_ABI, functionName: "pools", args: token ? [token] : undefined, chainId: polygon.id, query: { enabled: !!FAIR_LAUNCH_POOL_ADDRESS && !!token } });

  const decimals = Number(tokenDecimals ?? 18);
  const formattedBalance = useMemo(() => tokenBalance === undefined ? "" : formatUnits(tokenBalance as bigint, decimals), [tokenBalance, decimals]);
  const poolExists = !!existingPool && (existingPool as readonly unknown[])[1] !== "0x0000000000000000000000000000000000000000";

  useEffect(() => {
    if (formattedBalance && !tokenAmount) setTokenAmount(formattedBalance);
  }, [formattedBalance, tokenAmount]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isConnected || !address || !walletClient || !publicClient) {
      toast({ title: "Wallet Not Connected", description: "Connect the wallet that owns the tokens.", variant: "destructive" });
      return;
    }
    if (!FAIR_LAUNCH_POOL_ADDRESS) {
      toast({ title: "Fair launch unavailable", description: "The fair-launch pool contract is not configured.", variant: "destructive" });
      return;
    }
    if (!token || !tokenAmount || Number(tokenAmount) <= 0 || Number(duration) <= 0 || Number(target) <= 0) {
      toast({ title: "Invalid launch settings", description: "Enter a valid token address, amount, duration, and MATIC target.", variant: "destructive" });
      return;
    }
    if (poolExists) {
      toast({ title: "Pool already exists", description: "This token already has a fair-launch pool.", variant: "destructive" });
      return;
    }

    try {
      setIsSubmitting(true);
      if (!(await ensureCorrectNetwork())) return;
      const amount = parseUnits(tokenAmount, decimals);
      if (tokenBalance !== undefined && amount > (tokenBalance as bigint)) {
        toast({ title: "Insufficient token balance", description: `You can deposit up to ${formattedBalance} ${String(tokenSymbol || "tokens")}.`, variant: "destructive" });
        return;
      }
      const approvalHash = await walletClient.writeContract({ address: token, abi: erc20Abi, functionName: "approve", args: [FAIR_LAUNCH_POOL_ADDRESS, amount], chain: polygon });
      await publicClient.waitForTransactionReceipt({ hash: approvalHash });
      const createHash = await walletClient.writeContract({ address: FAIR_LAUNCH_POOL_ADDRESS, abi: FAIR_LAUNCH_POOL_ABI, functionName: "createPool", args: [token, amount, parseEther("0.01"), parseEther("0.001"), BigInt(duration), parseEther(target)], chain: polygon });
      await publicClient.waitForTransactionReceipt({ hash: createHash });
      const depositHash = await walletClient.writeContract({ address: FAIR_LAUNCH_POOL_ADDRESS, abi: FAIR_LAUNCH_POOL_ABI, functionName: "depositTokens", args: [token], chain: polygon });
      await publicClient.waitForTransactionReceipt({ hash: depositHash });

      const listingId = token.toLowerCase();
      const { error } = await (supabaseExtended as any).from("fair_launch_coins").upsert({
        id: listingId,
        name: String(tokenName || tokenSymbol || "Existing Token"),
        symbol: String(tokenSymbol || "TOKEN"),
        description: "Existing token listed through a fair-launch pool.",
        contract_address: token,
        creator_address: address.toLowerCase(),
        initial_supply: tokenAmount,
        transaction_hash: createHash,
        fair_launch: true,
        launch_pool_address: FAIR_LAUNCH_POOL_ADDRESS,
        launch_pool_transaction: depositHash,
      }, { onConflict: "contract_address" });
      if (error) console.error("Fair-launch listing storage failed:", error);
      toast({ title: "Existing token listed", description: `${String(tokenSymbol || "Token")} is now in a fair-launch pool.` });
      setTokenAddress("");
      setTokenAmount("");
    } catch (error) {
      toast({ title: "Fair-launch failed", description: error instanceof Error ? error.message : "The token could not be listed.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle>Fair-launch an existing token</CardTitle>
        <CardDescription>Use tokens already in your wallet to create a fair-launch pool. The pool can only be created once per token address.</CardDescription>
      </CardHeader>
      <CardContent>
        <Alert className="mb-5">
          <AlertTitle>Token custody</AlertTitle>
          <AlertDescription>The selected amount is transferred into the fair-launch pool after you approve the contract. Only list tokens you control.</AlertDescription>
        </Alert>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2"><Label htmlFor="existingTokenAddress">Token contract address</Label><Input id="existingTokenAddress" placeholder="0x..." value={tokenAddress} onChange={(event) => setTokenAddress(event.target.value)} /></div>
          {validTokenAddress && <p className="text-sm text-muted-foreground">Detected: {String(tokenName || "Unknown token")} {tokenSymbol ? `(${String(tokenSymbol)})` : ""} · Wallet balance: {formattedBalance || "loading..."}</p>}
          <div className="space-y-2"><Label htmlFor="existingTokenAmount">Amount to fair-launch</Label><Input id="existingTokenAmount" type="number" min="0" step="any" value={tokenAmount} onChange={(event) => setTokenAmount(event.target.value)} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"><div className="space-y-2"><div className="flex items-center gap-1"><Label htmlFor="existingLaunchDuration">Duration (seconds)</Label><TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="h-4 w-4 text-muted-foreground cursor-help" /></TooltipTrigger><TooltipContent><p>How long users can commit MATIC. 86,400 seconds equals 24 hours. The launch can also finalize early if the MATIC target is reached.</p></TooltipContent></Tooltip></TooltipProvider></div><Input id="existingLaunchDuration" type="number" min="3600" value={duration} onChange={(event) => setDuration(event.target.value)} /><p className="text-xs text-muted-foreground">{formatDurationHours(duration)}</p></div><div className="space-y-2"><div className="flex items-center gap-1"><Label htmlFor="existingLaunchTarget">MATIC target</Label><TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="h-4 w-4 text-muted-foreground cursor-help" /></TooltipTrigger><TooltipContent><p>The total MATIC commitment threshold for early finalization. For example, 20 means the launch can finalize once commitments reach 20 MATIC.</p></TooltipContent></Tooltip></TooltipProvider></div><Input id="existingLaunchTarget" type="number" min="0.01" step="any" value={target} onChange={(event) => setTarget(event.target.value)} /></div></div>
          {poolExists && <p className="text-sm text-destructive">This token already has a fair-launch pool.</p>}
          <Button type="submit" className="w-full" disabled={isSubmitting || poolExists}>{isSubmitting ? "Listing token..." : "List Existing Token"}</Button>
        </form>
      </CardContent>
    </Card>
  );
}
